import { Head, router } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { ConversationList } from "@/components/messaging/conversation-list"
import { CreatorPickerSheet } from "@/components/messaging/creator-picker-sheet"
import {
  ThreadEmptyState,
  ThreadPane,
} from "@/components/messaging/thread-pane"
import { usePollOnFocus } from "@/hooks/use-poll-on-focus"
import AppLayout from "@/layouts/app-layout"
import { crewMessageMessagesPath } from "@/routes"
import type { BrandMessagesPageProps } from "@/types/messaging"

/**
 * OKS-37 — Brand-side messaging page (`Crew/Messages/Index`).
 *
 * Single Inertia component renders BOTH `/crew/messages` (list-only,
 * empty right pane) and `/crew/messages/:crew_membership_id` (list +
 * detail). The discriminator is `currentMembershipId` — present on
 * the detail URL, absent on the list URL.
 *
 * Inertia partial reload swaps right-pane props on row navigation
 * (see `<Link only={[...]}>` in `<ConversationList>`), so the list
 * pane state (search query, scroll position) persists across the
 * URL change.
 *
 * Polling: `usePollOnFocus` reloads `conversations`, `messages`,
 * `currentConversation`, `unread_messages_count` every 30s while
 * the tab is visible (TRD §1).
 */
export default function BrandMessagesIndex(props: BrandMessagesPageProps) {
  const { t } = useTranslation()
  const [pickerOpen, setPickerOpen] = useState(false)

  usePollOnFocus({
    intervalMs: 30_000,
    only: [
      "conversations",
      "messages",
      "currentConversation",
      "currentMembership",
      "canReply",
      "unread_messages_count",
    ],
    // OKS-819 — test-support-only seam for the poll-no-yank E2E.
    exposeTestReloadHandle: "__oksMessagesReload",
  })

  function openPicker() {
    setPickerOpen(true)
    // Lazy-load picker options on demand (Inertia deferred prop).
    router.reload({ only: ["crewMembersForPicker"] })
  }

  return (
    <AppLayout>
      <Head title={t("messaging.brand.page_title")} />
      <div
        // OKS-820 — var-free flex fill (was `h-[calc(100vh-4rem)]
        // min-h-[480px]`, a fixed chrome guess that ignored the banner stack +
        // SidebarInset inset margin and left the composer ~81px below the fold).
        // The `BrandShell` now bounds the whole console chain to the viewport on
        // this route, so `flex-1 min-h-0` consumes exactly the remaining height;
        // the thread history (`ThreadPane`'s inner `flex-1 min-h-0` band) is the
        // single internal scroller and the composer stays pinned in view. No
        // fixed-height magic number — that guess was the root cause.
        className="flex min-h-0 w-full flex-1"
        data-testid="messaging-brand-page"
      >
        <ConversationList
          conversations={props.conversations}
          currentMembershipId={props.currentMembershipId}
          onPickCreator={openPicker}
          // On mobile, hide the list when a thread is selected (UX Spec §5).
          className={
            props.currentMembershipId !== null ? "hidden lg:flex" : "flex"
          }
        />

        <div
          className={
            props.currentMembershipId === null
              ? "hidden lg:flex lg:flex-1"
              : "flex flex-1"
          }
        >
          {props.currentMembership === null ? (
            <ThreadEmptyState
              message={t("messaging.brand.list.empty_description")}
            />
          ) : (
            <ThreadPane
              conversation={props.currentConversation}
              membership={props.currentMembership}
              messages={props.messages}
              canReply={props.canReply}
              composerLimit={props.composerLimit}
              sendUrl={crewMessageMessagesPath(props.currentMembership.id)}
            />
          )}
        </div>

        <CreatorPickerSheet
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          options={props.crewMembersForPicker}
        />
      </div>
    </AppLayout>
  )
}
